import type { Container, Point } from 'pixi.js';
import { Signal } from 'typed-signals';
import { lerp } from '@softgames/math-utils';
import { GAME_OBJECT_POOL } from '../GameConstants';
import MathUtils from '../../utils/MathUtils';
import { interpolationMap } from '../../libs/TweenManager/interpolations';
import { soundManager } from '../../app/SoundManager';
import type { EventScheduler } from '../../utils/EventScheduler';
import type { TaskScoreBubbleView } from './TaskScoreBubbleView';

export class TaskScoreBubbleAnimator {
  public readonly startWinParticles = new Signal();

  constructor(private readonly scheduler: EventScheduler) {}

  public async animateToGoal(bubble: TaskScoreBubbleView, target: Point): Promise<void> {
    await this.standardBubbleIn(bubble);
    await this.standardToTarget(bubble, target);
  }

  public async animateFinalBubble(bubble: TaskScoreBubbleView, target: Point): Promise<void> {
    this.startWinParticles.emit();
    await this.finalBubbleIn(bubble);
    await this.standardToTarget(bubble, target);
  }

  private async standardBubbleIn(bubble: TaskScoreBubbleView, scale?: number): Promise<void> {
    const scaleTo = scale ?? bubble.scaleX;
    bubble.props({ alpha: 0, scale: 0 });

    await Promise.all([
      bubble.tweenTo({ alpha: 1 }, 250, 'quadOut', true, 100).asyncOnComplete(),
      bubble.tweenTo({ y: bubble.y - 100 }, 600, 'cubicOut', true, 100).asyncOnComplete(),
      bubble.tweenTo({ scaleX: scaleTo, scaleY: scaleTo }, 500, 'sinOut', true, 100).asyncOnComplete(),
    ]);
  }

  private async finalBubbleIn(bubble: TaskScoreBubbleView): Promise<void> {
    const { scheduler } = this;

    await this.standardBubbleIn(bubble, 1.3);

    soundManager.play('Gameplay_Final_Score_Trigger');

    await scheduler.wait(500);

    void bubble.fadeBackground?.(0, 250, 0, 1.5);
  }

  public async standardToTarget(bubble: Container, globalTo: Point): Promise<void> {
    if (!bubble.parent) return;

    const localTo = bubble.parent.toLocal(globalTo, undefined, GAME_OBJECT_POOL.getPoint());
    const distance = MathUtils.getDistance(bubble.x, bubble.y, localTo.x, localTo.y);
    const animationDuration = 500 + Math.min(distance / 800, 1) * 500;

    const turnRight = bubble.x - localTo.x > 0;

    const midPointX = Math.max(bubble.x, localTo.x) + 300 * (turnRight ? 1 : -1);

    const prevPosition = { x: bubble.x, y: bubble.y };

    bubble.tweenTo(
      {
        scaleX: 0.5,
        scaleY: 0.5,
      },
      animationDuration * 0.5,
      'sinIn',
      true,
      animationDuration * 0.5,
    );

    let lerpValue = 0.01;

    bubble.tweens.createValueTween(lerpValue, 0.4, animationDuration, (value) => {
      lerpValue = value;
    });

    await bubble
      .tweenTo(
        {
          x: [bubble.x, midPointX, localTo.x],
          y: [bubble.y, bubble.y - distance * 0.5, localTo.y],
        },
        animationDuration,
        'sinIn',
        true,
      )
      .interpolation(interpolationMap.bezier)
      .onUpdateCallback(() => {
        bubble.rotation = lerp(
          bubble.rotation,
          MathUtils.getAngleToVector(prevPosition.x, prevPosition.y, bubble.x, bubble.y) + Math.PI * 0.5,
          lerpValue,
        );
        prevPosition.x = bubble.x;
        prevPosition.y = bubble.y;
      })
      .asyncOnComplete();

    GAME_OBJECT_POOL.addPoint(localTo);
  }
}
